// Orquestrador de cupons: mostra o formulário intermediário e direciona o submit
// Regras implementadas:
// - Exibe o orquestrador quando o DiscountYard carregar OU após 5s de timeout
// - Ao submeter, se começar com TROCA, RETIRADA, UNIFORME, 40MENSAL, ZRZS-DUPLA, MAIS, TESTE-
//   ou com algum prefixo configurável (data-native-discount-prefixes), envia para o input nativo;
//   caso contrário para o DiscountYard
// As regras em si (prefixos, bloqueios, mensagens) vêm de assets/discount-coupon-rules.js
// (@theme/discount-coupon-rules), compartilhadas com o carrinho da adição facilitada;
// o bloqueio de requisições do DiscountYard vem de @theme/discountyard-request-blocker.

import * as rules from '@theme/discount-coupon-rules';
import { blockDiscountyardRequests, unblockDiscountyardRequests } from '@theme/discountyard-request-blocker';

function showOrchestrator() {
  const form = document.getElementById('cart-discount-orchestrator');
  if (form) form.classList.add('show');
}

/**
 * @returns {{ form: HTMLFormElement | null, input: HTMLInputElement | null }}
 */
function getNativeDiscountFormAndInput() {
  const el = document.getElementById('cart-discount');
  if (!(el instanceof HTMLInputElement)) return { form: null, input: null };
  const form = el.closest('form');
  return {
    form: form instanceof HTMLFormElement ? form : null,
    input: el,
  };
}

/**
 * @returns {{ form: HTMLFormElement | null, input: HTMLInputElement | null, button: HTMLButtonElement | null }}
 */
function getDiscountYardInputAndForm() {
  // Seletores específicos do DiscountYard conforme informado
  const input = document.getElementById('coupons_stacker_input');
  const button = document.getElementById('coupons_stacker_add_button');
  const form = input ? input.closest('form') : null;
  return {
    form: form instanceof HTMLFormElement ? form : null,
    input: input instanceof HTMLInputElement ? input : null,
    button: button instanceof HTMLButtonElement ? button : null,
  };
}

function showLoadingSpinner() {
  const button = document.getElementById('cart-discount-orchestrator-button');
  if (button instanceof HTMLButtonElement) {
    button.classList.add('loading');
    button.disabled = true;
  }
}

function hideLoadingSpinner() {
  const button = document.getElementById('cart-discount-orchestrator-button');
  if (button instanceof HTMLButtonElement) {
    button.classList.remove('loading');
    button.disabled = false;
  }
}

/**
 * Tipo do cupom nativo ativo no carrinho (TROCA, RETIRADA, UNIFORME, 40MENSAL,
 * ZRZS-DUPLA, MAIS, TESTE ou CUSTOM pros prefixos configuráveis), ou null.
 * @returns {string | null}
 */
function getActiveNativeDiscountType() {
  const summaryContainer = document.querySelector('.cart__summary-container');
  if (!summaryContainer?.classList.contains('has-troca-discount')) return null;

  const pillCode = document.querySelector('.cart-discount__pill-code');
  if (!pillCode) return null;

  const codeText = (pillCode.textContent || '').trim().toUpperCase();
  return rules.getNativeDiscountType(codeText, getNativePrefixes());
}

/** @returns {string[]} prefixos nativos configuráveis do form do orquestrador */
function getNativePrefixes() {
  const form = document.getElementById('cart-discount-orchestrator');
  if (!(form instanceof HTMLFormElement)) return [];
  return rules.parsePrefixList(form.dataset.nativeDiscountPrefixes || '');
}

/**
 * Cupom nativo: prefixos chumbados OU configuráveis
 * @param {string} code
 * @returns {boolean}
 */
function isNativeDiscountCode(code) {
  return rules.isNativeDiscountCode(code, getNativePrefixes());
}

/**
 * @param {string} code
 * @returns {boolean}
 */
function validateIscaCumulativeRule(code) {
  const form = document.getElementById('cart-discount-orchestrator');
  if (!(form instanceof HTMLFormElement)) return true;

  const passes = rules.passesIscaCumulativeRule(code, {
    nativePrefixes: getNativePrefixes(),
    iscaPrefixes: rules.parsePrefixList(form.dataset.iscaCumulativePrefixes || ''),
    hasLineLevelDiscounts: form.dataset.hasLineLevelDiscounts === 'true',
  });
  if (passes) return true;

  showNonCumulativeDiscountError();
  return false;
}

function showNonCumulativeDiscountError() {
  const errorElement = document.getElementById('cart-discount-orchestrator-error');
  if (!errorElement) return;

  errorElement.textContent = rules.DEFAULT_MESSAGES.notCumulative;
  errorElement.classList.remove('hidden');
}

/**
 * Mostra o erro de cupom não combinável
 * @param {string} type - Tipo do cupom ativo (rules.getNativeDiscountType)
 */
function showOrchestratorError(type) {
  const errorElement = document.getElementById('cart-discount-orchestrator-error');
  if (!errorElement) return;

  const message = rules.notCombinableMessage(type);

  errorElement.textContent = message;
  errorElement.classList.remove('hidden');
}

/**
 * Esconde o erro do orquestrador
 */
function hideOrchestratorError() {
  const errorElement = document.getElementById('cart-discount-orchestrator-error');
  if (errorElement) {
    errorElement.classList.add('hidden');
    errorElement.textContent = '';
  }
}

function setupSubmitRouting() {
  // Delegação no documento para sobreviver a trocas de HTML
  document.addEventListener('submit', function (event) {
    const form =
      event.target instanceof Element &&
      event.target.closest('#cart-discount-orchestrator');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const input = document.getElementById('cart-discount-orchestrator-input');
    if (!(input instanceof HTMLInputElement)) return;

    const code = (input.value || '').trim();
    if (!code) return;

    hideOrchestratorError();

    // Bloqueia códigos PROMODEZ25 e PROMODEZ25-LENTES
    if (rules.isBlockedCode(code)) {
      const errorElement = document.getElementById('cart-discount-orchestrator-error');
      if (errorElement) {
        errorElement.textContent = rules.DEFAULT_MESSAGES.blocked;
        errorElement.classList.remove('hidden');
      }
      hideLoadingSpinner();
      return;
    }

    showLoadingSpinner();

    if (!validateIscaCumulativeRule(code)) {
      hideLoadingSpinner();
      return;
    }

    // Cupom nativo (TROCA/RETIRADA/... ou prefixos configuráveis) ativo não combina com DiscountYard
    const activeNativeType = getActiveNativeDiscountType();
    const isNewCodeNative = isNativeDiscountCode(code);
    if (activeNativeType && !isNewCodeNative) {
      showOrchestratorError(activeNativeType);
      hideLoadingSpinner();
      return;
    }

    // Define timeout de 3 segundos para esconder o spinner
    const loadingTimeout = setTimeout(() => {
      hideLoadingSpinner();
    }, 3000);

    const isNative = isNewCodeNative;

    // Atualiza classes no container do resumo do carrinho
    const summary = document.querySelector('.cart__summary-container');
    if (summary instanceof HTMLElement) {
      if (isNative) {
        summary.classList.add('has-troca-discount');
        summary.classList.remove('no-troca-discount');
      } else {
        // Se já tiver classes de troca ativas, mantém-as sem trocar
        // Só atualiza se não tiver classes de troca
        if (!summary.classList.contains('has-troca-discount')) {
          summary.classList.remove('has-troca-discount');
          summary.classList.add('no-troca-discount');
        }
      }
    }

    if (isNative) {
      // Bloqueia o DiscountYard para cupons nativos
      blockDiscountyardRequests();
      const nativeRefs = getNativeDiscountFormAndInput();
      if (!nativeRefs.form || !nativeRefs.input) {
        clearTimeout(loadingTimeout);
        hideLoadingSpinner();
        return;
      }
      nativeRefs.input.value = code;
      // requestSubmit dispara o handler on:submit="/applyDiscount" do component
      if (typeof nativeRefs.form.requestSubmit === 'function') {
        nativeRefs.form.requestSubmit();
      } else {
        const submitButton = nativeRefs.form.querySelector(
          'button[type="submit"]'
        );
        if (submitButton instanceof HTMLElement) {
          submitButton.click();
        } else {
          nativeRefs.form.dispatchEvent(
            new Event('submit', {
              bubbles: true,
              cancelable: true,
            })
          );
        }
      }
      return;
    }

    const dyRefs = getDiscountYardInputAndForm();
    if (dyRefs.input) {
      // Desbloqueia DiscountYard para cupons não TROCA
      unblockDiscountyardRequests();
      dyRefs.input.value = code;
      dyRefs.input.dispatchEvent(new Event('input', { bubbles: true }));
      dyRefs.input.dispatchEvent(new Event('change', { bubbles: true }));

      if (dyRefs.form) {
        const submitEvent2 = new Event('submit', {
          bubbles: true,
          cancelable: true,
        });
        dyRefs.form.dispatchEvent(submitEvent2);
      } else if (dyRefs.button) {
        dyRefs.button.click();
      }
    }

    // Em qualquer caso, o spinner será escondido pelo timeout
  });
}

function setupFallbackBehavior() {
  // Timeout de 5s para exibir o orquestrador caso o DiscountYard não apareça
  const timeoutId = setTimeout(showOrchestrator, 5000);

  const observer = new MutationObserver(function () {
    // Quando o DiscountYard (container ou wrapper) aparecer, mostramos o orquestrador
    const dyReady =
      document.getElementById('coupons_stacker_input') ||
      document.getElementById('coupons_stacker_add_button') ||
      document.querySelector('.stack-discounts-input-wrapper') ||
      document.getElementById('stack-discounts-container');
    if (dyReady) {
      clearTimeout(timeoutId);
      showOrchestrator();
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function setupClickToHideError() {
  // Remove o erro ao clicar em qualquer lugar da página
  document.addEventListener('click', function (event) {
    // Não remove o erro se o clique foi no botão de submit do orquestrador
    // (para permitir que a validação aconteça primeiro)
    const submitButton = document.getElementById('cart-discount-orchestrator-button');
    if (submitButton && event.target instanceof Node && submitButton.contains(event.target)) {
      return;
    }
    hideOrchestratorError();
  });
}

/**
 * Usa o flag do Liquid (data-has-line-level-discounts), que já ignora CLIPON-CONJUNTO
 * @returns {boolean}
 */
function hasBlockingAutomaticDiscount() {
  const form = document.getElementById('cart-discount-orchestrator');
  return (
    form instanceof HTMLFormElement &&
    form.dataset.hasLineLevelDiscounts === 'true'
  );
}

/**
 * @param {string} code
 * @returns {boolean}
 */
function matchesAllowedCumulativePrefix(code) {
  const form = document.getElementById('cart-discount-orchestrator');
  if (!(form instanceof HTMLFormElement)) return false;

  return rules.startsWithAny(
    code,
    rules.parsePrefixList(form.dataset.iscaCumulativePrefixes || '')
  );
}

/**
 * Remove cupons manuais do DiscountYard que não estão nos prefixos cumulativos permitidos
 * @returns {boolean} true se algum cupom foi removido
 */
function removeAllManualCoupons() {
  const manualTags = document.querySelectorAll(
    '.stack-discounts-tag[data-is-automatic="0"]'
  );

  let removedAny = false;
  manualTags.forEach(tag => {
    const code = (tag.getAttribute('data-value') || tag.textContent || '')
      .trim()
      .toUpperCase();

    if (matchesAllowedCumulativePrefix(code)) return;

    const closeButton = tag.querySelector('.stack-discounts-close-tag');
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
      removedAny = true;
    }
  });

  return removedAny;
}

/**
 * Se o Liquid indicar desconto automático bloqueante,
 * remove todos os cupons manuais do DiscountYard
 */
function enforceManualCouponRemovalOnAutomaticDiscount() {
  if (!hasBlockingAutomaticDiscount()) return;
  removeAllManualCoupons();
}

function setupAutomaticDiscountCouponGuard() {
  let debounceId = /** @type {ReturnType<typeof setTimeout> | null} */ (null);

  const runGuard = () => {
    if (debounceId) clearTimeout(debounceId);
    // Espera um pouco para o DiscountYard renderizar/atualizar as tags
    debounceId = setTimeout(
      enforceManualCouponRemovalOnAutomaticDiscount,
      300
    );
  };

  const observer = new MutationObserver(runGuard);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-has-line-level-discounts'],
  });

  // Executa uma vez no carregamento inicial
  runGuard();
}

document.addEventListener('DOMContentLoaded', function () {
  setupSubmitRouting();
  setupFallbackBehavior();
  setupClickToHideError();
  setupAutomaticDiscountCouponGuard();
});
